import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAxiosInstance } from '@gov-nx/api';
import { useMessageEvents } from '@gov-nx/core/events';
import { useLocale } from '@gov-nx/core/service';
import { useServiceAuthorization } from '@gov-nx/core/hooks';
import type { Nullable } from '@gov-nx/core/types';
import { hasProperty } from '@gov-nx/core/types';
import { pageRouteResolver } from '@gov-nx/web';
import type { PageCode } from '../../definitions/codes';
import type { MyAuthorizationDetailContextType } from './context.types';
import { useForm602SubmissionDeleteQuery } from './hooks/useForm602SubmissionDeleteQuery';
import { useForm602SubmissionQuery } from './hooks/useForm602SubmissionQuery';
import { useForm602SubmissionSendQuery } from './hooks/useForm602SubmissionSendQuery';
import { useAuthorizationForm } from './useAuthorizationForm';
import { useAutorizaceContext } from 'libs/component/web/src/Autorizace/Context';

const MojeAutorizaceDetailContext =
	createContext<Nullable<MyAuthorizationDetailContextType>>(null);

interface MojeAutorizaceDetailContextProviderProps {
	code: PageCode;
	formularPodaniId: string;
	children: React.ReactNode;
}

export function MojeAutorizaceDetailContextProvider({
	code,
	formularPodaniId,
	children,
}: MojeAutorizaceDetailContextProviderProps) {
	const queryClient = useQueryClient();
	const { toastMessageSuccess, toastMessageNeutral } = useMessageEvents();
	const { t } = useLocale(code);
	const navigate = useNavigate();
	const { axiosInstance } = useAxiosInstance();

	const authContext = useAutorizaceContext();

	const form602Submission = useForm602SubmissionQuery({ formularPodaniId });

	const serviceName = (() => {
		if (!form602Submission.data?.typ) return undefined;

		const isForm = form602Submission.data.typ.startsWith('F60');

		return isForm
			? `FORMS_${form602Submission.data.typ}`
			: `PODANI_${form602Submission.data.formId}`;
	})();

	const sendQuery = useForm602SubmissionSendQuery({
		onSuccess: async (data) => {
			const onNonHighAuthorizationFlow = async () => {
				await queryClient.invalidateQueries({
					queryKey: ['my-submissions-filter'],
				});
				await queryClient.invalidateQueries({
					queryKey: ['form602-submissions'],
				});
				toastMessageSuccess(t('autorizace.formular.odeslan'));
				formDefinition.formReset();
				navigate(pageRouteResolver('moje-podani'));
				await form602Submission.invalidateQuery();
			};
			if (!serviceName) {
				return;
			}
			authContext?.onInitAuthorization(
				serviceName,
				data.polozkaAutorizaceId,
				onNonHighAuthorizationFlow
			);
		},
	});

	const deleteQuery = useForm602SubmissionDeleteQuery({
		formularPodaniId,
		onSuccess: async () => {
			await queryClient.invalidateQueries(['form602-submissions']);
			toastMessageNeutral(t('autorizace.formular.smazan'), {
				icon: { name: 'trash', type: 'native' },
			});
			formDefinition.formReset();
			navigate(pageRouteResolver('moje-podani.autorizace'));
			await form602Submission.invalidateQuery();
		},
	});

	const isLoading = sendQuery.isLoading || deleteQuery.isLoading;

	const { isLowAuthorization, isAuthorizationEnforced } =
		useServiceAuthorization(serviceName ?? '');

	const formDefinition = useAuthorizationForm({
		isLoading,
		isLowAuthorization,
	});

	useEffect(() => {
		// TODO: this is hotfix solution bc RHF is 💩 and it doesn't update field
		if (form602Submission.data) {
			formDefinition.formReset();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [form602Submission.data, formDefinition.formMethods]);

	const handleSubmit = formDefinition.formMethods.handleSubmit(() => {
		sendQuery.mutate({
			axiosInstance,
			formularPodaniId,
			body: {
				akce: 'PODAT',
				prijemceDSId: form602Submission.data?.prijemce?.idDS,
			},
		});
	});

	return (
		<MojeAutorizaceDetailContext.Provider
			value={{
				code,
				form602Submission,
				formDefinition,
				attachments: form602Submission.data?.prilohy?.filter(
					hasProperty('dokumentId')
				),
				isProcessing: isLoading,
				sendQuery,
				deleteQuery,
				handleSubmit,
				isLowAuthorization,
				isAuthorizationEnforced,
			}}>
			{children}
		</MojeAutorizaceDetailContext.Provider>
	);
}

export const useMojeAutorizaceDetailContext =
	(): MyAuthorizationDetailContextType =>
		useContext(MojeAutorizaceDetailContext) as MyAuthorizationDetailContextType;
